import { useState, useRef, useEffect } from 'react';
import { useContainerDimensions, useUuid } from '../hooks';
import {
  UseDropDownArguments,
  UseDropDownReturnType,
  GetSelectedValueArguments,
  DropDownOption,
  DropDownValue,
  CalculatePopUpDimensionsArguments,
  CalculatePopUpDimensionsReturnType,
} from './types';

function getSelectedValue({ options = [], value }: GetSelectedValueArguments) {
  return options.find(option => option.value === value);
}

export const itemHeight = 40;
export const maxNumOfItems = 4;
export const popUpMargin = 15;

function calculatePopUpDimensions({
  inputDimensions,
  numberOfOptions,
  viewPortHeight,
}: CalculatePopUpDimensionsArguments): CalculatePopUpDimensionsReturnType {
  const numberOfCurrentItems = Math.min(maxNumOfItems, numberOfOptions);

  const popUpRequiredHeight = numberOfCurrentItems * itemHeight;

  const remainingSpaceBelow = viewPortHeight - inputDimensions.bottom;
  const popUpFitsBelowInput = remainingSpaceBelow >= popUpRequiredHeight;

  if (!popUpFitsBelowInput) {
    return {
      width: inputDimensions.width,
      top: inputDimensions.top - popUpMargin - popUpRequiredHeight,
      left: inputDimensions.left,
    };
  }
  return {
    width: inputDimensions.width,
    top: inputDimensions.bottom + popUpMargin,
    left: inputDimensions.left,
  };
}

export const useDropDown = <T extends DropDownValue = DropDownValue>({
  value,
  options,
  disabled,
  onChange,
  onOpen,
  placeholder,
}: UseDropDownArguments<T>): UseDropDownReturnType<T> => {
  const inputId = useUuid();
  const [selectedOption, setSelectedOption] = useState(getSelectedValue({ options, value }));
  const [popupIsOpened, setPopUpIsOpened] = useState(false);

  const inputRef = useRef<HTMLHeadingElement>(null);
  const popupRef = useRef<HTMLUListElement>(null);

  const { dimensions: inputDimensions } = useContainerDimensions({ ref: inputRef });

  const popupDimensions = calculatePopUpDimensions({
    numberOfOptions: options?.length || 0,
    viewPortHeight: document.documentElement.clientHeight,
    inputDimensions,
  });

  const switchPopUpOpen = () => {
    if (!disabled) {
      setPopUpIsOpened(!popupIsOpened);
    }
  };

  const onPopUpItemClick = (option: DropDownOption<T>) => {
    if (option !== selectedOption) {
      setSelectedOption(option);
      onChange?.(option.value);
    }

    setPopUpIsOpened(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (!popupIsOpened) return;

    const clickInsideInput = inputRef.current && (inputRef.current as any).contains(event.target);
    if (clickInsideInput) return;

    const clickOutsideOfPopup =
      popupRef.current && !(popupRef.current as any).contains(event.target);

    if (clickOutsideOfPopup) {
      setPopUpIsOpened(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  useEffect(() => {
    if (popupIsOpened) {
      onOpen?.();
    }
  }, [popupIsOpened]);

  useEffect(() => {
    setSelectedOption(getSelectedValue({ options, value }));
  }, [value, selectedOption, options]);

  const inputValue = selectedOption?.text || placeholder || '';

  return {
    inputValue,
    selectedOption,
    inputId,
    popupDimensions,
    inputRef,
    popupRef,
    onPopUpItemClick,
    switchPopUpOpen,
    popupIsOpened,
  };
};
